import { Button, Modal, Tippy, TextField } from '@superops/ui-design-system';
import { useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import {
  getTranslatedText,
  toast,
  DeleteModal,
  TEXT_LENGTH,
  Logo as ModuleLogo,
  useActivityTracker
} from '@nile/components';
import { preFormatFilterObject } from '@nile/reports/src/modules/component/helper';
import envConfig from '@nile/home/src/Config/env';
import { AccountClient } from '@nile/graphql';
import { DELETE_CHART } from '@nile/reports/src/modules/charts/graphql';
import { EXPORT_REPORT } from '@nile/reports/src/modules/DataReports/graphql';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { cloneDeep, isEmpty } from 'lodash';
import ChartContext from '@nile/reports/src/modules/charts/contexts/Chart';
import SaveContainer from '@nile/reports/src/modules/charts/components/subpages/ChartHeader/SaveContainer';
import ChartHomeContext from '@nile/reports/src/modules/charts/contexts/Chart/home';
import { SVGIcon } from '@superops/super-icons';
import ReportsContext from '@nile/reports/src/modules/home/contexts/Reports';
import { HeaderContainer } from './styles';

const ChartHeader = (props) => {
  const context = useContext(ChartContext);
  const charthomecontext = useContext(ChartHomeContext);
  const reportsContext = useContext(ReportsContext);

  const [active, setActive] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState(false);

  const {
    errors,
    submitActionLoading,
    originalData,
    intl,
    disabledSave,
    handleSave,
    hideSave,
    saveAsOnly,
    hideDelete
  } = props;

  const [deleteChart] = useMutation(DELETE_CHART, {
    client: AccountClient,
    onCompleted(res) {
      if (res?.deleteChart) {
        toast('success', 'reports.widget.deleteWidget.success');
      }
    }
  });

  const handleChange = (prop, newValue) => {
    const updatedData = cloneDeep(context.headerValue);
    updatedData[prop] = newValue;
    context.updateHeaderValue(updatedData);
  };

  const handleDelete = () => {
    deleteChart({
      variables: {
        chartId: context.value.chartId
      }
    }).then(() => {
      reportsContext.setChartListMeta({
        refresh: true,
        data: { resetPage: true }
      });
      charthomecontext.closeModal();
    });
  };

  const exportCSV = async () => {
    let variable = {
      chartId: context.value.chartId
    };
    if (reportsContext.reportCondition[reportsContext.activeView]) {
      const { operands, reportCondition } = await preFormatFilterObject({
        entity: context?.value?.entity,
        filterObj: cloneDeep(
          reportsContext.reportFilter?.current &&
            reportsContext.reportFilter?.current?.[reportsContext.activeView]
        ),
        removeLabel: true
      });

      variable = {
        ...variable,
        condition:
          (reportCondition &&
            !isEmpty(operands) && {
              joinOperator: 'AND',
              operands
            }) ||
          null
      };
    }
    AccountClient.query({
      query: EXPORT_REPORT,
      variables: {
        ExportReportIdentifierInput: variable
      },
      fetchPolicy: 'network-only'
    })
      .then((res) => {
        if (res?.data?.exportReport) {
          useActivityTracker({ event: 'EXPORT_CHART' });
          toast('success', 'app.toast.charts.exportCsv.success');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setActive(false));
  };

  const modalActivator = (
    <Tippy
      interactive
      title={getTranslatedText(intl, 'common.delete')}
      position="bottom-end"
    >
      <Button
        icon={<SVGIcon name="Delete" />}
        onClick={() => {
          if (charthomecontext.removeChart) {
            charthomecontext?.removeChart({
              chartId: context.value.chartId,
              isRemove: true
            });
            charthomecontext.closeModal();
          }
        }}
      />
    </Tippy>
  );

  const closeConfirmationModal = () => {
    if (!context.isUpdated()) {
      setCloseConfirmation(true);
      return;
    }
    charthomecontext.closeModal();
  };

  const onDiscard = () => {
    charthomecontext.closeModal();
  };

  return (
    <HeaderContainer className="fx actr fx-sb bg-white p25 position-relative brTransparent">
      <div className="flex f11 actr">
        <ModuleLogo
          className="mr10"
          url={`${envConfig.imageRoot}svg/Charts.svg`}
        />
        <div className="fx fcol f11">
          <TextField
            placeholder={getTranslatedText(
              intl,
              'reports.chart.title.placeholder'
            )}
            value={context?.headerValue?.name}
            onChange={(val) => handleChange('name', val)}
            error={errors?.name?.[0]}
            autoFocus
            bgTransparent
            fontSize="fontLarge"
            maxLength={TEXT_LENGTH.size_5}
            noborder
          />
          <div className="description">
            <TextField
              placeholder={getTranslatedText(
                intl,
                'reports.chart.description.placeholder'
              )}
              value={context?.headerValue?.description}
              onChange={(val) => handleChange('description', val)}
              error={errors?.description?.[0]}
              maxLength={TEXT_LENGTH.size_10}
              bgTransparent
              noborder
            />
          </div>
        </div>
      </div>
      <div className="flex f11 jend g10">
        <div className="fx actr">
          <Modal
            activator={
              <Button onClick={() => closeConfirmationModal()} monochrome plain>
                <FormattedMessage id="common.close" />
              </Button>
            }
            open={closeConfirmation}
            onClose={() => setCloseConfirmation(false)}
            variant="confirmation"
            title={
              <FormattedMessage id="reports.chart.create.modal.discardChanges" />
            }
            primaryAction={{
              content: (
                <div className="flex actr">
                  <FormattedMessage id="common.button.discardChanges" />
                </div>
              ),
              onAction: onDiscard
            }}
            secondaryActions={[
              {
                content: <FormattedMessage id="common.cancel" />,
                onAction: () => setCloseConfirmation(false)
              }
            ]}
          />
        </div>
        {context?.value?.chartId && (
          <Modal
            activator={
              <Tippy
                interactive
                title={getTranslatedText(intl, 'common.exportCsv')}
                position="bottom-end"
              >
                <Button
                  onClick={() => setActive(true)}
                  icon={<SVGIcon name="Download" />}
                />
              </Tippy>
            }
            open={active}
            onClose={() => setActive(false)}
            variant="confirmation"
            title={
              <FormattedMessage id="reports.chart.exportCsv.modal.header" />
            }
            primaryAction={{
              content: (
                <div className="flex actr">
                  <SVGIcon name="CircleTickOutlineMinor" />
                  <div className="ml8">
                    <FormattedMessage id="invoice.details.list.taxable" />
                  </div>
                </div>
              ),
              onAction: exportCSV
            }}
            secondaryActions={[
              {
                content: <FormattedMessage id="common.cancel" />,
                onAction: () => setActive(false)
              }
            ]}
          >
            <FormattedMessage id="reports.chart.exportCsv.modal.description" />
          </Modal>
        )}
        {!hideDelete && (
          <>
            <DeleteModal
              modalActivator={modalActivator}
              description={getTranslatedText(
                intl,
                'reports.chart.deleteItem.modalDescription'
              )}
              handleDeleteConfirmation={handleDelete}
              variant="confirmation"
              title={getTranslatedText(intl, 'reports.widget.deleteItem.title')}
              sectioned={false}
            />
          </>
        )}
        <SaveContainer
          hideSave={hideSave}
          handleSave={handleSave}
          saveAsOnly={saveAsOnly}
          disabledSave={disabledSave}
          submitActionLoading={submitActionLoading}
          layoutRef={charthomecontext?.layoutRef}
          originalData={originalData}
        />
      </div>
    </HeaderContainer>
  );
};

export default withRouter(injectIntl(ChartHeader));
